import apiClient from "@/api-client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { apiErrorMessage } from "@/lib/admin-formatters";
import { logger } from "@/lib/default-logger";

/**
 * The curated voice catalogue.
 *
 * One short list, set once at platform level, inherited by every account — the same shape as the
 * price book: `voice` is a public-schema table and a row in it changes what every workspace is
 * offered. So there are two audiences and two resources behind them, and the split is deliberate:
 *
 * `GET /admin/voices` is super-admin only and answers with the whole row — provider, the
 * provider's own voice id, curation notes, retired voices included.
 *
 * `GET /voices` is what a client reads in the wizard: active voices only, in the admin's order,
 * carrying a name, a language, a persona and a sample URL. Which vendor synthesises a voice is
 * never in that response, and nothing here should try to put it back.
 *
 * Paths never start with `/api` — the axios baseURL already ends in it.
 */

/** The 5 MiB ceiling `AdminVoicesController.uploadSample` asks @fastify/multipart for. */
export const MAX_VOICE_SAMPLE_BYTES = 5 * 1024 * 1024;

/**
 * What the browser may offer in the file picker.
 *
 * A hint only, and never the check that matters: the API sniffs the bytes and refuses anything
 * that is not audio with a 415, because the declared type is caller-controlled and the sample is
 * served back inline.
 */
export const VOICE_SAMPLE_ACCEPT = "audio/*,.mp3,.m4a,.aac,.wav,.ogg,.opus,.webm,.flac";

/**
 * The provider that is not a provider — `UPLOAD_PROVIDER_KEY` in
 * `src/modules/campaign-requests/types/upload-provider.ts`, copied exactly.
 *
 * A voice whose provider is this one exists only as a file: a voice-to-voice clone, a reading
 * somebody made. It has no `providerVoiceId`, because there is no catalogue anywhere that lists
 * it, and nothing can be synthesised through it.
 */
export const UPLOAD_PROVIDER_KEY = "upload";

/** `MAX_SAMPLE_SCRIPT_LENGTH` from `voice.request.ts`. Providers bill by the character. */
export const MAX_SAMPLE_SCRIPT_LENGTH = 500;

/**
 * `DEFAULT_SAMPLE_SCRIPT` from `voices.service.ts`, copied exactly.
 *
 * Only ever a placeholder in a box somebody can edit. The API applies it to a voice created
 * without a script of its own — but only for a real provider: an uploaded recording is given no
 * default, because a line claiming to be what the recording says is a sentence nobody wrote.
 */
export const DEFAULT_SAMPLE_SCRIPT = "Hi, this is Grace calling from AdaptiveX. Have I caught you at an alright time?";

/** A curated voice, whole, as the admin who curates it sees it. */
export interface Voice {
	id: string;
	/** Our name for it, in the admin's own words. The only name a client ever reads. */
	displayName: string;
	/** The plugin's registered name — `elevenlabs`, `in-world-tts` — never a plugin id. */
	providerPluginKey: string;
	/** Null for `upload`: a recording has no catalogue id anywhere. */
	providerVoiceId: string | null;
	/** BCP 47. `en` also answers an `en-SG` agent — see `languageMatches` in `lib/languages`. */
	language: string;
	gender: string | null;
	persona: string | null;
	/** The line the sample says. Kept on the row so it can be reworded and spoken again. */
	sampleScript: string | null;
	isActive: boolean;
	position: number;
	hasSample: boolean;
	/** `/api/voices/<id>/sample`. Null where no sample has been recorded yet. */
	sampleUrl: string | null;
	sampleMimeType: string | null;
	sampleSizeBytes: number | null;
	/** For whoever curates the list. Never shown to a client. */
	notes: string | null;
	createdAt: string;
	updatedAt: string;
}

/**
 * The same voice as a client is offered it. Narrower on purpose — a client picks by the name we
 * gave it and by listening, so the provider and the curation notes are simply not in the response.
 */
export interface CatalogueVoice {
	id: string;
	displayName: string;
	language: string;
	gender: string | null;
	persona: string | null;
	sampleUrl: string | null;
}

export interface VoicePayload {
	displayName: string;
	providerPluginKey: string;
	/** Null where the provider is `upload` — the API stores no id for a voice that has none. */
	providerVoiceId: string | null;
	language: string;
	gender?: string | null;
	persona?: string | null;
	sampleScript?: string | null;
	isActive?: boolean;
	position?: number;
	notes?: string | null;
}

/** What the admin list may be narrowed by. Retired voices are always included. */
export interface AdminVoiceFilters {
	language?: string | null;
	providerPluginKey?: string | null;
}

export const voicesKeys = {
	all: ["voices"] as const,
	catalogues: () => [...voicesKeys.all, "catalogue"] as const,
	catalogue: (language?: string | null) => [...voicesKeys.catalogues(), { language: language ?? null }] as const,
	admin: () => [...voicesKeys.all, "admin"] as const,
	adminList: (filters?: AdminVoiceFilters) => [...voicesKeys.admin(), { filters: filters ?? null }] as const,
};

/**
 * The path to fetch a sample's bytes from.
 *
 * The API mints `sampleUrl` as `/api/voices/<id>/sample` so no screen has to know the route, but
 * the axios baseURL already ends in `/api` — so the prefix comes off before it is used, and the
 * server stays the one deciding what the route is.
 */
export function voiceSamplePath(sampleUrl: string): string {
	return sampleUrl.startsWith("/api/") ? sampleUrl.slice(4) : sampleUrl;
}

// ---------------------------------------------------------------------------------------------
// The client catalogue
// ---------------------------------------------------------------------------------------------

/**
 * The active catalogue, in the admin's curation order.
 *
 * `language` is left off by default so one call answers every language on a request. Filtering is
 * the caller's job through `voiceMatchesLanguage`.
 */
export function useVoiceCatalogue(options: { language?: string | null; enabled?: boolean } = {}) {
	const { language = null, enabled = true } = options;

	return useQuery({
		queryKey: voicesKeys.catalogue(language),
		queryFn: async (): Promise<CatalogueVoice[]> => {
			const response = await apiClient.get("/voices", { params: language ? { language } : undefined });
			return response.data;
		},
		enabled,
		// The catalogue changes when a super-admin curates it, which is not on any timescale a
		// wizard session cares about. Refetching it per step mount is noise.
		staleTime: 5 * 60 * 1000,
	});
}

// ---------------------------------------------------------------------------------------------
// Curation — super-admin only, and the API says so too
// ---------------------------------------------------------------------------------------------

/** The whole catalogue, retired voices included. A 403 here is a legitimate answer, not a bug. */
export function useAdminVoices(filters: AdminVoiceFilters = {}, enabled = true) {
	return useQuery({
		queryKey: voicesKeys.adminList(filters),
		queryFn: async (): Promise<Voice[]> => {
			const response = await apiClient.get("/admin/voices", {
				params: {
					...(filters.language ? { language: filters.language } : {}),
					...(filters.providerPluginKey ? { providerPluginKey: filters.providerPluginKey } : {}),
				},
			});
			return response.data;
		},
		enabled,
	});
}

export function useCreateVoice() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (data: VoicePayload): Promise<Voice> => {
			const response = await apiClient.post("/admin/voices", data);
			return response.data;
		},
		onSuccess: async () => {
			await queryClient.invalidateQueries({ queryKey: voicesKeys.all });
			toast.success("Voice added");
		},
		onError: (error: any) => {
			logger.error(error);
			toast.error(apiErrorMessage(error, "Could not add the voice"));
		},
	});
}

export function useUpdateVoice() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({ id, data }: { id: string; data: Partial<VoicePayload> }): Promise<Voice> => {
			const response = await apiClient.patch(`/admin/voices/${id}`, data);
			return response.data;
		},
		onSuccess: async () => {
			await queryClient.invalidateQueries({ queryKey: voicesKeys.all });
			toast.success("Voice saved");
		},
		onError: (error: any) => {
			logger.error(error);
			toast.error(apiErrorMessage(error, "Could not save the voice"));
		},
	});
}

/**
 * Curation order, as a list of ids in the order they should appear.
 *
 * Positions are rewritten from the array index by the API, so the whole of a language's list is
 * sent rather than the two rows that swapped — an ordering expressed as separate positions can
 * disagree with itself.
 */
export function useReorderVoices() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (ids: string[]): Promise<Voice[]> => {
			const response = await apiClient.put("/admin/voices/order", { ids });
			return response.data;
		},
		// `onSettled`, not `onSuccess`: the list is reordered on screen before the write is
		// answered, so a refusal has to pull the server's order back or the rows stay where they
		// were dropped and the next drag is computed from an order nobody saved.
		onSettled: async () => {
			await queryClient.invalidateQueries({ queryKey: voicesKeys.all });
		},
		onError: (error: any) => {
			logger.error(error);
			toast.error(apiErrorMessage(error, "Could not save the order"));
		},
	});
}

/**
 * Have the provider speak the voice's script, and keep what it said as the sample.
 *
 * One request that holds until the vendor has finished — up to a minute, and the axios client
 * sets no timeout, so it is the API's own 408 that decides when to give up rather than a browser
 * cutting the wire and leaving nobody sure whether a sample was made.
 *
 * No success toast, and no error toast either: this is the only mutation on the screen whose
 * result is a thing you look at rather than a thing that happened. The drawer shows the run and
 * shows the refusal in place, next to the script that caused it — `error` on the mutation is what
 * it reads. A toast would be a sentence that scrolls away about a player that is still empty.
 */
export function useGenerateVoiceSample() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({ id, script }: { id: string; script?: string }): Promise<Voice> => {
			const response = await apiClient.post(`/admin/voices/${id}/sample/generate`, script ? { script } : {});
			return response.data;
		},
		onSuccess: async () => {
			await queryClient.invalidateQueries({ queryKey: voicesKeys.all });
		},
		onError: (error: any) => {
			logger.error(error);
		},
	});
}

/** An uploaded recording. The other way to a sample is generating one; see above. */
export function useUploadVoiceSample() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({ id, file }: { id: string; file: File }): Promise<Voice> => {
			const formData = new FormData();
			formData.append("file", file);

			const response = await apiClient.post(`/admin/voices/${id}/sample`, formData, {
				headers: { "Content-Type": "multipart/form-data" },
			});
			return response.data;
		},
		onSuccess: async () => {
			await queryClient.invalidateQueries({ queryKey: voicesKeys.all });
			toast.success("Sample uploaded");
		},
		onError: (error: any) => {
			logger.error(error);
			// 413 over the 5 MiB ceiling, 415 where the bytes are not an audio file a browser plays.
			toast.error(apiErrorMessage(error, "Could not upload the sample"));
		},
	});
}

/** Destroys the bytes. The voice stays curated, and silent, until a new sample is sent. */
export function useRemoveVoiceSample() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (id: string): Promise<Voice> => {
			const response = await apiClient.delete(`/admin/voices/${id}/sample`);
			return response.data;
		},
		onSuccess: async () => {
			await queryClient.invalidateQueries({ queryKey: voicesKeys.all });
			toast.success("Sample removed");
		},
		onError: (error: any) => {
			logger.error(error);
			toast.error(apiErrorMessage(error, "Could not remove the sample"));
		},
	});
}

/**
 * Take a voice off the list.
 *
 * The API answers `{ deleted }` rather than a 204 because the two outcomes are different: a voice
 * some request has already chosen is retired instead of destroyed, so the row that says what a
 * client agreed to survives. The toast says which happened rather than claiming a deletion.
 */
export function useDeleteVoice() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (id: string): Promise<{ deleted: boolean }> => {
			const response = await apiClient.delete(`/admin/voices/${id}`);
			return response.data;
		},
		onSuccess: async (result) => {
			await queryClient.invalidateQueries({ queryKey: voicesKeys.all });
			toast.success(result.deleted ? "Voice deleted" : "Voice retired — it stays on requests that already chose it");
		},
		onError: (error: any) => {
			logger.error(error);
			toast.error(apiErrorMessage(error, "Could not remove the voice"));
		},
	});
}
